import {useCallback, useEffect, useState} from 'react';
import {AxiosError} from 'axios';
import {ApiErrorBody, registerInlineUpgradeSurface, shouldPromptUpgrade} from '../support/upgradePrompt';

/** Объяснение premium-гейта для inline-панели {@code PremiumUpsell}. */
export interface PremiumGate {
    title: string;
    description?: string;
}

/**
 * Premium-гейт экрана или виджета (R17).
 *
 * Экран, данные которого закрыты тарифом, не должен показывать «Не удалось загрузить …
 * Повторить»: 402/403 — это не сбой, и повтор всегда упрётся в тот же гейт. Хук отделяет
 * гейт от настоящей ошибки и сразу объявляет экран «объясняющим себя», поэтому глобальный
 * баннер {@code UpgradePrompt} на тот же отказ не поднимается.
 *
 * Регистрация происходит на монтировании, а не в момент появления гейта: интерцептор
 * публикует событие синхронно внутри упавшего запроса — к моменту {@code setState} баннер
 * был бы уже показан.
 */
export const usePremiumGate = () => {
    const [premiumGate, setPremiumGate] = useState<PremiumGate | null>(null);

    useEffect(() => registerInlineUpgradeSurface(), []);

    /**
     * Разбирает ошибку запроса: если это premium-гейт — запоминает объяснение и возвращает
     * {@code true}; иначе ничего не меняет и возвращает {@code false} (обрабатывайте как сбой).
     */
    const capturePremiumGate = useCallback((e: unknown, fallbackTitle: string): boolean => {
        const error = e as AxiosError;
        const body = error?.response?.data as ApiErrorBody | undefined;
        if (!shouldPromptUpgrade(error?.response?.status, body)) {
            return false;
        }
        setPremiumGate({title: body?.error || fallbackTitle, description: body?.description});
        return true;
    }, []);

    const resetPremiumGate = useCallback(() => setPremiumGate(null), []);

    return {premiumGate, capturePremiumGate, resetPremiumGate};
};
